import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { useState, useEffect, createContext, useContext } from "react";
import axios from "axios";

// Pages
import HomePage from "@/pages/HomePage";
import NoticiasPage from "@/pages/NoticiasPage";
import NoticiaDetailPage from "@/pages/NoticiaDetailPage";
import CholutecaPage from "@/pages/CholutecaPage";
import HondurasPage from "@/pages/HondurasPage";
import DestinosPage from "@/pages/DestinosPage";
import ExperienciasPage from "@/pages/ExperienciasPage";
import SaboresPage from "@/pages/SaboresPage";
import VideosPage from "@/pages/VideosPage";
import ContactoPage from "@/pages/ContactoPage";
import MemoriasPage from "@/pages/MemoriasPage";
import MemoriaDetailPage from "@/pages/MemoriaDetailPage";
import CompartirPage from "@/pages/CompartirPage";
import GuiasPage from "@/pages/GuiasPage";
import GuiaDetailPage from "@/pages/GuiaDetailPage";
import GaleriasPage from "@/pages/GaleriasPage";
import GaleriaDetailPage from "@/pages/GaleriaDetailPage";
import AntesAhoraPage from "@/pages/AntesAhoraPage";
import ArtistasPage from "@/pages/ArtistasPage";
import ArtistaDetailPage from "@/pages/ArtistaDetailPage";
import DirectorioPage from "@/pages/DirectorioPage";
import BusinessDetailPage from "@/pages/BusinessDetailPage";
import MapaPage from "@/pages/MapaPage";
import DeportesPage from "@/pages/DeportesPage";
import TorneoPage from "@/pages/TorneoPage";
import EquipoPage from "@/pages/EquipoPage";
import EquiposPage from "@/pages/EquiposPage";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminPosts from "@/pages/admin/AdminPosts";
import AdminPostEdit from "@/pages/admin/AdminPostEdit";
import AdminMedia from "@/pages/admin/AdminMedia";
import AdminImport from "@/pages/admin/AdminImport";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminMemorias from "@/pages/admin/AdminMemorias";
import AdminMemoriaEdit from "@/pages/admin/AdminMemoriaEdit";
import AdminSubmissions from "@/pages/admin/AdminSubmissions";
import AdminGuias from "@/pages/admin/AdminGuias";
import AdminGalerias from "@/pages/admin/AdminGalerias";
import AdminAntesAhora from "@/pages/admin/AdminAntesAhora";
import AdminArtistas from "@/pages/admin/AdminArtistas";
import AdminDirectorio from "@/pages/admin/AdminDirectorio";
import AdminRedesSociales from "@/pages/admin/AdminRedesSociales";
import AdminTienda from "@/pages/admin/AdminTienda";
import AdminEquipos from "@/pages/admin/AdminEquipos";
import AdminTorneos from "@/pages/admin/AdminTorneos";
import AdminPartidos from "@/pages/admin/AdminPartidos";
import AdminPortadas from "@/pages/admin/AdminPortadas";
import AdminNewsletter from "@/pages/admin/AdminNewsletter";
import AdminUsuarios from "@/pages/admin/AdminUsuarios";
import AdminEmprendedores from "@/pages/admin/AdminEmprendedores";
import AdminPublicidad from "@/pages/admin/AdminPublicidad";
import AdminContenidoComunitario from "@/pages/admin/AdminContenidoComunitario";
import AdminContacto from "@/pages/admin/AdminContacto";
import AdminTransmisiones from "@/pages/admin/AdminTransmisiones";
import TiendaPage from "@/pages/TiendaPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import CheckoutPage from "@/pages/CheckoutPage";
import EmprendedoresPage from "@/pages/EmprendedoresPage";
import PublicidadPage from "@/pages/PublicidadPage";
import CompartirContenidoPage from "@/pages/CompartirContenidoPage";
import ReportarNoticiaPage from "@/pages/ReportarNoticiaPage";
import TransmisionesEnVivoPage from "@/pages/TransmisionesEnVivoPage";
import PartidoDetailPage from "@/pages/PartidoDetailPage";
import DestinoDetailPage from "@/pages/DestinoDetailPage";
import AdminDestinos from "@/pages/admin/AdminDestinos";
import AdminEnVivo from "@/pages/admin/AdminEnVivo";
import AdminMarketplace from "@/pages/admin/AdminMarketplace";
import EnVivoPage from "@/pages/EnVivoPage";
import MarketplacePage from "@/pages/MarketplacePage";
import MarketplaceAuthPage from "@/pages/MarketplaceAuthPage";
import MarketplaceDashboard from "@/pages/MarketplaceDashboard";
import MarketplaceProductDetail from "@/pages/MarketplaceProductDetail";
import EventosPage from "@/pages/EventosPage";
import EventoDetailPage from "@/pages/EventoDetailPage";
import AdminEventos from "@/pages/admin/AdminEventos";
import AdminReportes from "@/pages/admin/AdminReportes";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Set default timeout for all axios requests (30 seconds for mobile connections)
axios.defaults.timeout = 30000;

// Configure axios request interceptor to add auth token from localStorage
axios.interceptors.request.use(
  (config) => {
    // Add token from localStorage if available and making request to our API
    const token = localStorage.getItem("session_token");
    if (token && config.url?.includes('/api')) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Configure axios interceptor for handling expired sessions
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Clear local storage and redirect to login
      localStorage.removeItem("session_token");
      // Only redirect if we're on an admin page
      if (window.location.pathname.startsWith('/admin') && !window.location.pathname.includes('/admin/login')) {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider - V2 with localStorage backup
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      // Get token from localStorage - this is the primary auth method for mobile
      const storedToken = localStorage.getItem("session_token");
      
      // If no stored token, user is not authenticated
      if (!storedToken) {
        setUser(null);
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${API}/auth/me`, {
        headers: {
          "Authorization": `Bearer ${storedToken}`
        },
        // Don't use withCredentials when we have a token - this avoids Safari cookie issues
        withCredentials: false,
        timeout: 15000 // 15 second timeout for auth check
      });
      setUser(response.data);
    } catch (error) {
      console.log("Auth check failed:", error.response?.status || error.message);
      // Clear invalid token
      localStorage.removeItem("session_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    const storedToken = localStorage.getItem("session_token");
    
    // Clear local storage first to ensure user is logged out even if API call fails
    localStorage.removeItem("session_token");
    setUser(null);
    
    // Try to invalidate session on server (fire and forget)
    if (storedToken) {
      try {
        await axios.post(`${API}/auth/logout`, {}, { 
          headers: { "Authorization": `Bearer ${storedToken}` },
          timeout: 5000
        });
      } catch (error) {
        // Ignore logout errors - user is already logged out locally
        console.log("Server logout notification failed (user is logged out locally)");
      }
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-bg">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/noticias" element={<NoticiasPage />} />
      <Route path="/noticias/:slug" element={<NoticiaDetailPage />} />
      <Route path="/choluteca" element={<CholutecaPage />} />
      <Route path="/honduras" element={<HondurasPage />} />
      <Route path="/destinos" element={<DestinosPage />} />
      <Route path="/destinos/:slug" element={<DestinoDetailPage />} />
      <Route path="/experiencias" element={<ExperienciasPage />} />
      <Route path="/experiencias/:slug" element={<NoticiaDetailPage />} />
      <Route path="/sabores" element={<SaboresPage />} />
      <Route path="/sabores/:slug" element={<NoticiaDetailPage />} />
      <Route path="/videos" element={<VideosPage />} />
      <Route path="/videos/:slug" element={<NoticiaDetailPage />} />
      <Route path="/memorias" element={<MemoriasPage />} />
      <Route path="/memorias/:slug" element={<MemoriaDetailPage />} />
      <Route path="/compartir" element={<CompartirPage />} />
      <Route path="/guias" element={<GuiasPage />} />
      <Route path="/guias/:slug" element={<GuiaDetailPage />} />
      <Route path="/galerias" element={<GaleriasPage />} />
      <Route path="/galerias/:slug" element={<GaleriaDetailPage />} />
      <Route path="/antes-ahora" element={<AntesAhoraPage />} />
      <Route path="/artistas" element={<ArtistasPage />} />
      <Route path="/artistas/:slug" element={<ArtistaDetailPage />} />
      <Route path="/directorio" element={<DirectorioPage />} />
      <Route path="/directorio/:slug" element={<BusinessDetailPage />} />
      <Route path="/mapa" element={<MapaPage />} />
      <Route path="/en-vivo" element={<EnVivoPage />} />
      <Route path="/marketplace" element={<MarketplacePage />} />
      <Route path="/marketplace/registro" element={<MarketplaceAuthPage />} />
      <Route path="/marketplace/mis-productos" element={<MarketplaceDashboard />} />
      <Route path="/marketplace/:slug" element={<MarketplaceProductDetail />} />
      <Route path="/eventos" element={<EventosPage />} />
      <Route path="/eventos/:slug" element={<EventoDetailPage />} />
      <Route path="/contacto" element={<ContactoPage />} />
      <Route path="/reportar" element={<ReportarNoticiaPage />} />
      <Route path="/reportar-noticia" element={<ReportarNoticiaPage />} />
      
      {/* Deportes Routes */}
      <Route path="/deportes" element={<DeportesPage />} />
      <Route path="/deportes/equipos" element={<EquiposPage />} />
      <Route path="/deportes/en-vivo" element={<TransmisionesEnVivoPage />} />
      <Route path="/deportes/partido/:matchId" element={<PartidoDetailPage />} />
      <Route path="/deportes/torneo/:slug" element={<TorneoPage />} />
      <Route path="/deportes/equipo/:slug" element={<EquipoPage />} />
      
      {/* Tienda Routes */}
      <Route path="/tienda" element={<TiendaPage />} />
      <Route path="/tienda/checkout" element={<CheckoutPage />} />
      <Route path="/tienda/:slug" element={<ProductDetailPage />} />

      {/* New Forms */}
      <Route path="/emprendedores" element={<EmprendedoresPage />} />
      <Route path="/publicidad" element={<PublicidadPage />} />
      <Route path="/compartir-contenido" element={<CompartirContenidoPage />} />

      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/posts"
        element={
          <ProtectedRoute>
            <AdminPosts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/posts/new"
        element={
          <ProtectedRoute>
            <AdminPostEdit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/posts/:postId"
        element={
          <ProtectedRoute>
            <AdminPostEdit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/media"
        element={
          <ProtectedRoute>
            <AdminMedia />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/import"
        element={
          <ProtectedRoute>
            <AdminImport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/categories"
        element={
          <ProtectedRoute>
            <AdminCategories />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/memorias"
        element={
          <ProtectedRoute>
            <AdminMemorias />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/memorias/:memoriaId"
        element={
          <ProtectedRoute>
            <AdminMemoriaEdit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/submissions"
        element={
          <ProtectedRoute>
            <AdminSubmissions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/guias"
        element={
          <ProtectedRoute>
            <AdminGuias />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/guias/new"
        element={
          <ProtectedRoute>
            <AdminGuias />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/guias/:guiaId"
        element={
          <ProtectedRoute>
            <AdminGuias />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/galerias"
        element={
          <ProtectedRoute>
            <AdminGalerias />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/antes-ahora"
        element={
          <ProtectedRoute>
            <AdminAntesAhora />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/artistas"
        element={
          <ProtectedRoute>
            <AdminArtistas />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/directorio"
        element={
          <ProtectedRoute>
            <AdminDirectorio />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/destinos"
        element={
          <ProtectedRoute>
            <AdminDestinos />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/en-vivo"
        element={
          <ProtectedRoute>
            <AdminEnVivo />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/marketplace"
        element={
          <ProtectedRoute>
            <AdminMarketplace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/redes-sociales"
        element={
          <ProtectedRoute>
            <AdminRedesSociales />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tienda"
        element={
          <ProtectedRoute>
            <AdminTienda />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/equipos"
        element={
          <ProtectedRoute>
            <AdminEquipos />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/torneos"
        element={
          <ProtectedRoute>
            <AdminTorneos />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/partidos"
        element={
          <ProtectedRoute>
            <AdminPartidos />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/transmisiones"
        element={
          <ProtectedRoute>
            <AdminTransmisiones />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/portadas"
        element={
          <ProtectedRoute>
            <AdminPortadas />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/newsletter"
        element={
          <ProtectedRoute>
            <AdminNewsletter />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/usuarios"
        element={
          <ProtectedRoute>
            <AdminUsuarios />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/emprendedores"
        element={
          <ProtectedRoute>
            <AdminEmprendedores />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/publicidad"
        element={
          <ProtectedRoute>
            <AdminPublicidad />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/contenido-comunitario"
        element={
          <ProtectedRoute>
            <AdminContenidoComunitario />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/contacto"
        element={
          <ProtectedRoute>
            <AdminContacto />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/eventos"
        element={
          <ProtectedRoute>
            <AdminEventos />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/reportes"
        element={
          <ProtectedRoute>
            <AdminReportes />
          </ProtectedRoute>
        }
      />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </AuthProvider>
  );
}

export default App;
